import { Close, Search, Sync } from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { CouvertureDto, PatientReadSourceEnum } from '../../common/interfaces'
import { CouvertureFormRef } from '../../containers/app/billing/form/CouvertureForm'
import { useAppDispatch, useAppSelector } from '../../hooks/reduxHooks'
import {
  AldResponse,
  AldiService,
  SearchAldiWithCarteVitaleDTO,
} from '../../services/AldiService'
import { MemberDto } from '../../services/api'
import { CreatePatientDTO } from '../../services/dtos'
import StorageService from '../../services/storage'
import { doCreateALD, doFetchALD } from '../../state/reducers/aldReducer'
import { doGetMemberSituation } from '../../state/reducers/billingReducer'
import { PatientFormRef } from './PatientFormV2'

type Props = {
  open: boolean
  onClose: () => void
  patientFormRef: React.RefObject<PatientFormRef>
  coverageFormRef: React.RefObject<CouvertureFormRef>
  patientId?: number
}

const AldiSearchResult = ({
  open,
  onClose,
  patientFormRef,
  coverageFormRef,
  patientId,
}: Props) => {
  const [isSearching, setIsSearching] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<AldResponse | null>(null)
  const [selectedMember, setSelectedMember] = useState<MemberDto | null>(null)
  const [isSyncing, setIsSyncing] = useState(false)
  const [syncSuccess, setSyncSuccess] = useState<string | null>(null)

  const dispatch = useAppDispatch()
  const { memberSituation, aldList } = useAppSelector((state) => ({
    memberSituation: state.order.selectedMemberSituation,
    aldList: state.ald.list,
  }))

  useEffect(() => {
    if (!open) {
      return
    }
    const cachedData = StorageService.getMemberSituationCache()
    if (cachedData && cachedData.member) {
      setSelectedMember(cachedData.member)
    }

    // Fetch existing ALD records for the patient
    if (patientId) {
      dispatch(doFetchALD(patientId))
    }
  }, [open, patientId, dispatch])

  useEffect(() => {
    if (selectedMember && open) {
      dispatch(doGetMemberSituation(selectedMember.id))
        .unwrap()
        .catch(() => {
          setError('Erreur lors de la récupération de la situation du médecin')
        })
    }
  }, [selectedMember, dispatch, open])

  useEffect(() => {
    if (memberSituation && selectedMember) {
      StorageService.setMemberSituationCache(selectedMember, memberSituation)
    }
  }, [memberSituation, selectedMember])

  const getAldiBirthDate = (
    coverageValues: CouvertureDto | null | undefined,
    patientValues: CreatePatientDTO | null | undefined,
  ) => {
    const coverageBirth = coverageValues?.beneficaireDateNaissance
      ? moment(coverageValues.beneficaireDateNaissance, [
          'YYYY-MM-DD',
          'YYYYMMDD',
        ])
      : null
    if (coverageBirth && coverageBirth.isValid()) {
      return coverageBirth.format('YYYY-MM-DD')
    }

    const patientBirth = patientValues?.birthDate
      ? moment(patientValues.birthDate)
      : null
    if (patientBirth && patientBirth.isValid()) {
      return patientBirth.format('YYYY-MM-DD')
    }

    const patientBirthStr = patientValues?.birthDateStr
      ? moment(patientValues.birthDateStr, 'DD-MM-YYYY')
      : null
    if (patientBirthStr && patientBirthStr.isValid()) {
      return patientBirthStr.format('YYYY-MM-DD')
    }

    return ''
  }

  const handleSearchAldi = async () => {
    if (isSearching) {
      return
    }

    setError(null)
    setResult(null)
    setSyncSuccess(null)

    if (!memberSituation) {
      setError(
        'Veuillez sélectionner un médecin disposant d\'une situation valide avant d\'utiliser ADRi',
      )
      return
    }

    const patientValues = await patientFormRef.current?.getValues()
    if (!patientValues) {
      setError(
        'Veuillez compléter les informations du patient avant d\'utiliser ADRi',
      )
      return
    }

    const coverageValues = await coverageFormRef.current?.getValues(false)
    if (!coverageValues) {
      setError(
        'Veuillez compléter les informations de couverture ou lire une carte Vitale avant d\'utiliser ADRi',
      )
      return
    }

    const birthDate = getAldiBirthDate(coverageValues, patientValues)
    if (!birthDate) {
      setError(
        'La date de naissance du bénéficiaire est requise pour la recherche ADRi',
      )
      return
    }

    const sanitize = (value?: string | number | null) =>
      value !== undefined && value !== null
        ? String(value).replace(/\s+/g, '')
        : ''

    const psModel = {
      psNumeroFacturation:
        memberSituation.situation.numIdentificationPSFacturation,
      psIdentStructure: memberSituation.situation.numIdentificationStructure,
      authentificationMode: parseInt(
        process.env.REACT_APP_AUTH_MODE || '1',
        10,
      ),
      codePorteurCPS: process.env.REACT_APP_CODE_PORTEUR_CPS || '0',
      RPPS: memberSituation.member.rppsNumber || '',
    }

    const aldiPayload: SearchAldiWithCarteVitaleDTO = {
      psModel,
      aldiCV: {
        assureNumSecu:
          sanitize(coverageValues.assureNir) || sanitize(patientValues.ssn),
        beneficiaireDateNaissance: birthDate,
        beneficiaireRang: sanitize(coverageValues.rangNaissance) || '1',
        beneficiaireNumSecu: sanitize(coverageValues.beneficiaireNir),
        codeRegime: coverageValues.organismeAmo?.regime || '',
        codeCaisse: coverageValues.organismeAmo?.caisse || '',
        codeCentre: coverageValues.organismeAmo?.centre || '',
        numeroSerieCv: sanitize(coverageValues.numeroSerieCv),
      },
      fichierInjecte: {
        fichierBase64: '',
      },
      assertions: {
        assertionPs: '',
        assertionVitale: '',
        cpsBase64AuthCertificate: '',
        cpsBase64SignatureCertificate: '',
      },
      isApcv: coverageValues.readSource === PatientReadSourceEnum.Apcv,
      idJfse: memberSituation.situation.idJfse,
    }

    try {
      setIsSearching(true)
      const response = await AldiService.searchWithCarteVitale(aldiPayload)
      if (response.message.reponse?.erreur) {
        const errorMessage =
          response.message.reponse?.erreurMessage ||
          response.message.reponse?.erreurMessageComplet ||
          ''
        throw new Error(errorMessage)
      }

      const infoMessage =
        response.message.reponse?.erreurMessage ||
        response.message.reponse?.erreurMessageComplet

      if (infoMessage) {
        setSyncSuccess(infoMessage)
      }

      setResult(response)
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Une erreur est survenue lors de la récupération des ALD'
      setError(message)
    } finally {
      setIsSearching(false)
    }
  }

  const handleSyncAldi = async () => {
    if (!patientId) {
      setError('ID patient manquant pour la synchronisation')
      return
    }

    if (!result?.message?.listeAld || result.message.listeAld.length === 0) {
      setError('Aucune ALD à synchroniser')
      return
    }

    try {
      setIsSyncing(true)
      setSyncSuccess(null)
      setError(null)

      const newAlds = result.message.listeAld.filter((aldiAld) => {
        return !aldList.some((existingAld) => existingAld.code === aldiAld.code)
      })

      if (newAlds.length === 0) {
        setSyncSuccess('Toutes les ALDs sont déjà synchronisées')
        return
      }

      const createPromises = newAlds.map((aldiAld) => {
        const aldData = {
          libelle: aldiAld.libelle,
          code: aldiAld.code,
          etat: aldiAld.etat.toLowerCase() === 'oui' ? 'Actif' : 'Inactif',
          dateFin: aldiAld.dateFin || undefined,
          cimCode: aldiAld.codeCim || undefined,
          source: 'ALDi' as const,
          patientId,
        }

        return dispatch(doCreateALD(aldData as any)).unwrap()
      })

      await Promise.all(createPromises)
      setSyncSuccess(
        `${newAlds.length} ALD${newAlds.length > 1 ? 's' : ''} synchronisée${
          newAlds.length > 1 ? 's' : ''
        } avec succès`,
      )
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Une erreur est survenue lors de la synchronisation'
      setError(message)
    } finally {
      setIsSyncing(false)
    }
  }

  const resetForm = () => {
    setResult(null)
    setError(null)
    setSelectedMember(null)
    setSyncSuccess(null)
  }

  const handleClose = () => {
    if (isSearching) {
      return
    }
    resetForm()
    onClose()
  }

  const formatDate = (value: string | null) => {
    if (!value) return ''
    const parsed = moment(value, ['YYYY-MM-DD', 'YYYYMMDD', moment.ISO_8601])
    if (!parsed.isValid()) return ''

    // Check if the date is a placeholder/invalid date like 01/01/0001
    const year = parsed.year()
    if (year < 1900) return ''

    return parsed.format('DD/MM/YYYY')
  }

  const formatEtat = (etat: string | null | undefined) => {
    if (!etat) return 'Inactif'
    return etat.toLowerCase() === 'oui' ? 'Actif' : 'Inactif'
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: { height: '90vh' },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6">Recherche ALDi</Typography>
        <IconButton onClick={handleClose}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ my: 2, display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            onClick={handleSearchAldi}
            disabled={isSearching || !memberSituation}
            startIcon={
              isSearching ? <CircularProgress size={20} /> : <Search />
            }
          >
            {isSearching ? 'Recherche en cours...' : 'Rechercher ALDi'}
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleSyncAldi}
            disabled={
              isSyncing ||
              isSearching ||
              !result ||
              !result.message?.listeAld ||
              result.message.listeAld.length === 0 ||
              !patientId
            }
            startIcon={isSyncing ? <CircularProgress size={20} /> : <Sync />}
          >
            {isSyncing ? 'Synchronisation...' : 'Synchroniser'}
          </Button>
        </Box>

        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}
          {syncSuccess && <Alert severity="success">{syncSuccess}</Alert>}
          {!isSearching &&
            !error &&
            result &&
            (result.message?.listeAld?.length ?? 0) === 0 && (
              <Alert severity="info">
                Aucune ALD trouvée pour le bénéficiaire sélectionné.
              </Alert>
            )}
          {!isSearching &&
            !error &&
            (result?.message?.listeAld?.length || 0) > 0 && (
              <>
                <Typography variant="h6">Résultats de la recherche</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Code</TableCell>
                      <TableCell>Libellé</TableCell>
                      <TableCell>État</TableCell>
                      <TableCell>Date de début</TableCell>
                      <TableCell>Date de fin</TableCell>
                      <TableCell>Code CIM</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result?.message?.listeAld?.map((ald, index) => {
                      return (
                        <TableRow key={`${ald.code}-${index}`}>
                          <TableCell>{ald.code}</TableCell>
                          <TableCell>{ald.libelle}</TableCell>
                          <TableCell>{formatEtat(ald.etat)}</TableCell>
                          <TableCell>{formatDate(ald.dateDebut)}</TableCell>
                          <TableCell>{formatDate(ald.dateFin)}</TableCell>
                          <TableCell>{ald.codeCim}</TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
                <Stack spacing={1} sx={{ mt: 2 }}>
                  <Alert severity="info">
                    Le libellé de l&apos;ALD fourni par le service peut ne pas
                    être celui qui a été utilisé par le médecin traitant pour
                    remplir le protocole de soins.
                  </Alert>
                  <Alert severity="info">
                    La liste des ALD retournées peut ne pas être exhaustive dans
                    certains cas
                  </Alert>
                  {result?.message?.listeAld?.some(
                    (ald) => !ald.dateFin || formatDate(ald.dateFin) === '',
                  ) && (
                    <Alert severity="info">
                      Les informations relatives à l&apos;ALD du patient sont
                      anciennes et peuvent justifier l&apos;actualisation de son
                      protocole de soins
                    </Alert>
                  )}
                </Stack>
              </>
            )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={handleClose} disabled={isSearching}>
          Fermer
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AldiSearchResult
